import { initReactI18next } from 'react-i18next';

import { getLocale } from '@actual-app/core/shared/locale';
import * as Platform from '@actual-app/core/shared/platform';
import { setDefaultOptions } from 'date-fns';
import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

import { languages } from './languages';

/** Contabile default UI language. */
export const DEFAULT_LANGUAGE = 'it';

export const availableLanguages = Platform.isPlaywright
  ? []
  : Object.keys(languages).map(path => path.split('/')[2].split('.')[0]);

const isLanguageAvailable = (language: string) =>
  Object.hasOwn(languages, `/locale/${language}.json`);

const loadLanguage = (language: string) => {
  if (!isLanguageAvailable(language)) {
    throw new Error(`Unknown locale ${language}`);
  }
  return languages[`/locale/${language}.json`]();
};

void i18n
  .use(initReactI18next)
  .use(resourcesToBackend(loadLanguage))
  .init({
    // Bootstrap value only: keys are natural English, so this is what renders
    // before a catalogue is loaded. The app switches to DEFAULT_LANGUAGE as
    // soon as `setI18NextLanguage(null)` runs (see components/App.tsx).
    lng: 'en',

    // allow keys to be phrases having `:`, `.`
    nsSeparator: false,
    keySeparator: false,
    // do not load a fallback
    fallbackLng: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      transSupportBasicHtmlNodes: false,
    },
  });

const resolveLanguage = (language: string) => {
  // English is always available since we use natural-language keys.
  if (language === 'en') return 'en';

  if (isLanguageAvailable(language)) return language;

  const lowercaseLanguage = language.toLowerCase();
  if (lowercaseLanguage !== language) {
    console.info(
      `Unknown locale ${language}, falling back to ${lowercaseLanguage}`,
    );
    return resolveLanguage(lowercaseLanguage);
  }

  if (language.includes('-')) {
    const fallback = language.split('-')[0];
    console.info(`Unknown locale ${language}, falling back to ${fallback}`);
    return resolveLanguage(fallback);
  }

  return undefined;
};

export const setI18NextLanguage = (language: string | null) => {
  const defaultLanguages = Array.isArray(navigator.languages)
    ? navigator.languages
    : [navigator.language || 'en'];
  // Contabile: with no explicit user preference, Italian wins over the
  // browser's list; the browser languages stay as a secondary fallback.
  const languagesToTry = language
    ? [language]
    : [DEFAULT_LANGUAGE, ...defaultLanguages];

  let resolved: string | undefined;

  for (const lang of languagesToTry) {
    resolved = resolveLanguage(lang);
    if (resolved) break;
  }

  if (!resolved) {
    // Fall back to English
    console.info(
      language
        ? `Unknown locale ${language}, falling back to en`
        : `Unknown locales [${languagesToTry.join(', ')}] falling back to en`,
    );
    resolved = 'en';
  }

  // Contabile: date-fns is called in dozens of places without an explicit
  // `locale` option (calendar report, cash-flow axis, age of money, tooltips),
  // and those calls printed English month and weekday names inside an Italian
  // page. Setting the library default once keeps every one of them in step
  // with the UI language, including code that has no access to a React hook.
  // The locale object carries its own `weekStartsOn` (Monday for Italian), so
  // it is not set separately here.
  setDefaultOptions({ locale: getLocale(resolved) });

  if (resolved === i18n.language) {
    return; // language is already set
  }

  void i18n.changeLanguage(resolved);
};
